/**
 * Policy Evaluator
 * 
 * Pure function evaluator for policy-based authorization.
 * This is the core of the authorization system.
 * 
 * Evaluation pipeline (mandatory order):
 * 1. Capability check (existing scoped permissions)
 * 2. Policy evaluation (ordered rules, first match wins)
 * 3. Default deny (if no rule matches)
 * 
 * Key guarantees:
 * - Pure function: same inputs = same outputs
 * - No side effects: does not fetch data or modify state
 * - Deterministic: evaluates rules in defined order
 * - Explainable: every decision has reasons
 */

import type {
  ActorContext,
  ActionContext,
  ResourceContext,
  EvaluationContext,
  FullEvalContext,
  PolicyDecision,
  PolicyReason,
  PolicyRule,
  CapabilityChecker,
  ExplainResult,
} from './types';
import { evaluateAuthorizationV2 } from './policyEvaluatorV2';
import type { AuthorizationV1Delegates, AuthorizationDecision } from './policyEvaluatorV2';

// ============================================================================
// V2 Shadow Mode (internal)
// ============================================================================

/** Test-only: when set, overrides env so "shadow off" tests can run reliably (e.g. when Vite inlines import.meta.env). */
let _shadowEnabledForTesting: boolean | null = null;
export function setShadowEnabledForTesting(value: boolean | null): void {
  _shadowEnabledForTesting = value;
}

function isAuthzV2ShadowEnabled(): boolean {
  if (_shadowEnabledForTesting !== null) return _shadowEnabledForTesting;
  try {
    if (typeof process !== 'undefined' && process.env?.AUTHZ_V2_SHADOW === '1') return true;
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env?.VITE_AUTHZ_V2_SHADOW === '1') return true;
  } catch {
    // ignore
  }
  return false;
}

/** Structured diff when v1 and v2 decisions differ. Logged as JSON for observability. */
interface AuthzV2DiffPayload {
  action: string;
  resourceType?: string;
  resourceId?: string;
  principalId: string;
  context: string;
  v1: { result: 'allow' | 'deny'; reasons?: PolicyReason[] };
  v2: { result: 'allow' | 'deny'; reasons?: PolicyReason[]; engine: string };
  timestamp: string;
}

function logAuthzV2Diff(payload: AuthzV2DiffPayload): void {
  console.warn(JSON.stringify(payload));
}

/** Test-only: override delegates used in shadow mode so tests can force v1 ≠ v2. */
let _shadowDelegatesForTesting: AuthorizationV1Delegates | null = null;
export function setShadowDelegatesForTesting(delegates: AuthorizationV1Delegates | null): void {
  _shadowDelegatesForTesting = delegates;
}

function decisionsDiffer(v1: PolicyDecision, v2: AuthorizationDecision): boolean {
  if (v1.allowed !== v2.allowed) return true;
  if (v1.decision !== v2.decision) return true;
  if (v1.ruleId !== v2.ruleId) return true;
  const r1 = v1.reasons[0]?.code;
  const r2 = v2.reasons[0]?.code;
  if (r1 !== r2) return true;
  return false;
}

// ============================================================================
// Action Pattern Matching
// ============================================================================

/**
 * Check if an action matches a pattern.
 * Supports glob patterns:
 * - '*' matches everything
 * - 'items.*' matches 'items.edit', 'items.delete', etc.
 * - 'items.edit.*' matches 'items.edit.content', 'items.edit.status', etc.
 * - 'items.edit' matches only 'items.edit' exactly
 */
export function actionMatchesPattern(action: string, pattern: string): boolean {
  if (pattern === '*') {
    return true;
  }
  
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2);
    return action === prefix || action.startsWith(prefix + '.');
  }
  
  return action === pattern;
}

/**
 * Check if an action matches any of the patterns.
 */
export function actionMatchesAnyPattern(action: string, patterns: string[]): boolean {
  return patterns.some(pattern => actionMatchesPattern(action, pattern));
}

// ============================================================================
// Rule Matching
// ============================================================================

/**
 * Check if a rule applies to the given context.
 * A rule applies if:
 * - The action matches one of the rule's action patterns
 * - The resource type matches (if specified)
 * - The actor has one of the required roles (if specified)
 * - The 'when' predicate returns true
 */
export function ruleApplies(rule: PolicyRule, ctx: FullEvalContext): boolean {
  // Check action patterns
  if (!actionMatchesAnyPattern(ctx.action.key, rule.actions)) {
    return false;
  }
  
  // Check resource types (if specified)
  if (rule.resourceTypes && rule.resourceTypes.length > 0) {
    if (!rule.resourceTypes.includes(ctx.resource.type)) {
      return false;
    }
  }
  
  // Check roles (if specified)
  if (rule.roles && rule.roles.length > 0) {
    const hasRequiredRole = rule.roles.some(role => ctx.actor.roleIds.includes(role));
    if (!hasRequiredRole) {
      return false;
    }
  }
  
  // Check the predicate
  return rule.when(ctx);
}

// ============================================================================
// Main Evaluator
// ============================================================================

/**
 * Core v1 evaluation (no shadow). Used by evaluateAuthorization and as the
 * default delegate for v2 shadow mode to avoid recursion.
 * Exported for tests that need to build v1 delegates without triggering shadow.
 */
export function __evaluateAuthorizationCoreForTesting(
  actor: ActorContext,
  action: ActionContext,
  resource: ResourceContext,
  context: EvaluationContext,
  rules: PolicyRule[],
  capabilityChecker: CapabilityChecker
): PolicyDecision {
  const ctx: FullEvalContext = { actor, action, resource, context };
  const basePermission = getBasePermission(action.key);
  const hasCapability = capabilityChecker(basePermission, resource.scope);

  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'capability-check',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: basePermission } }],
      capabilityPassed: false,
    };
  }

  for (const rule of rules) {
    if (ruleApplies(rule, ctx)) {
      return {
        allowed: rule.effect === 'allow',
        decision: rule.effect,
        ruleId: rule.id,
        reasons: rule.reasons(ctx),
        capabilityPassed: true,
      };
    }
  }

  return {
    allowed: false,
    decision: 'deny',
    ruleId: 'default-deny',
    reasons: [{ code: 'NO_POLICY_MATCHED', data: { action: action.key } }],
    capabilityPassed: true,
  };
}

/**
 * Evaluate authorization for an action.
 * 
 * This is a pure function that takes all required context and returns a decision.
 * It does not fetch data or produce side effects.
 * 
 * Pipeline:
 * 1. Check capability (scoped permission)
 * 2. Evaluate policy rules (first match wins)
 * 3. Default deny if no rule matches
 * 
 * @param actor - Who is performing the action
 * @param action - What action is being attempted
 * @param resource - The resource being acted upon
 * @param context - Business context for the decision
 * @param rules - Ordered list of policy rules
 * @param capabilityChecker - Function to check scoped permissions
 */
export function evaluateAuthorization(
  actor: ActorContext,
  action: ActionContext,
  resource: ResourceContext,
  context: EvaluationContext,
  rules: PolicyRule[],
  capabilityChecker: CapabilityChecker
): PolicyDecision {
  const decision = __evaluateAuthorizationCoreForTesting(
    actor,
    action,
    resource,
    context,
    rules,
    capabilityChecker
  );

  if (decision.ruleId === 'default-deny' && decision.reasons[0]?.code === 'NO_POLICY_MATCHED') {
    const rawAction = action as unknown;
    const actionKey =
      typeof rawAction === 'string'
        ? rawAction
        : (rawAction as { key?: string } | null)?.key ?? null;
    const looseResource = resource as {
      exit_status?: unknown;
      detailedState?: unknown;
      detailed_state?: unknown;
      state?: unknown;
    } | null;
    console.warn('[AUTH NO_MATCH]', {
      action: actionKey,
      engine: null,
      actorRoleIds: actor.roleIds,
      actorUserId: actor.userId,
      contextPhase: (context as { phase?: unknown } | null | undefined)?.phase ?? null,
      resource: {
        id: (resource as { id?: string }).id ?? null,
        workspaceId: (resource as { workspaceId?: string; workspace_id?: string }).workspaceId ?? (resource as { workspace_id?: string }).workspace_id ?? null,
        createdByUserId: (resource as { createdByUserId?: string; created_by_user_id?: string }).createdByUserId ?? (resource as { created_by_user_id?: string }).created_by_user_id ?? null,
        ownerId: (resource as { ownerId?: string }).ownerId ?? null,
        assigneeId: (resource as { assigneeId?: string; shapingOwnerId?: string }).assigneeId ?? (resource as { shapingOwnerId?: string }).shapingOwnerId ?? (resource as { stewardId?: string }).stewardId ?? null,
      },
      resourceDetailedState: looseResource?.exit_status ?? looseResource?.detailedState ?? looseResource?.detailed_state ?? null,
      resourceState: looseResource?.state ?? null,
    });
  }

  // V2 shadow mode: run v2 and log diff only if v1 ≠ v2
  if (isAuthzV2ShadowEnabled()) {
    const input = { actor, action, resource, context, rules, capabilityChecker };
    const defaultDelegates: AuthorizationV1Delegates = {
      evaluateAuthorizationV1: __evaluateAuthorizationCoreForTesting,
      evaluateAndExplainV1: (a, ac, r, c, ru, cc) => {
        const d = __evaluateAuthorizationCoreForTesting(a, ac, r, c, ru, cc);
        return { decision: d, explanation: explainDecision(d) };
      },
    };
    const delegates = _shadowDelegatesForTesting ?? defaultDelegates;
    const v2Decision = evaluateAuthorizationV2(input, delegates);
    if (decisionsDiffer(decision, v2Decision)) {
      logAuthzV2Diff({
        action: action.key,
        resourceType: resource.type,
        resourceId: resource.id,
        principalId: actor.userId,
        context: context.cycleId ? 'CYCLE' : 'GLOBAL',
        v1: { result: decision.decision, reasons: decision.reasons },
        v2: { result: v2Decision.decision, reasons: v2Decision.reasons, engine: v2Decision.engine ?? 'v2-shadow-delegate' },
        timestamp: new Date().toISOString(),
      });
    }
  }

  return decision;
}

/**
 * Extract base permission from a detailed action key.
 * 
 * Examples:
 * - 'items.edit.content' -> 'items.edit'
 * - 'items.edit' -> 'items.edit'
 * - 'lists.publish' -> 'lists.publish'
 */
export function getBasePermission(actionKey: string): string {
  const parts = actionKey.split('.');
  
  // If there are more than 2 parts, return first 2
  // e.g., 'items.edit.content' -> 'items.edit'
  if (parts.length > 2) {
    return `${parts[0]}.${parts[1]}`;
  }
  
  return actionKey;
}

// ============================================================================
// Explain API
// ============================================================================

/**
 * Generate a user-friendly explanation of a policy decision.
 * This is used by UI to show "why is this disabled?"
 */
export function explainDecision(decision: PolicyDecision): ExplainResult {
  const primaryReason = decision.reasons[0];
  
  if (decision.allowed) {
    return {
      allowed: true,
      summaryKey: `reason.${primaryReason?.code ?? 'ALLOWED'}`,
      summary: getReasonSummary(primaryReason?.code ?? 'ALLOWED', primaryReason?.data),
      details: primaryReason?.data,
    };
  }
  
  // Build remediation hints based on reason
  const remediationHints = getRemediationHints(decision.reasons);
  
  return {
    allowed: false,
    summaryKey: `reason.${primaryReason?.code ?? 'DENIED'}`,
    summary: getReasonSummary(primaryReason?.code ?? 'DENIED', primaryReason?.data),
    details: primaryReason?.data,
    remediationHints,
  };
}

/**
 * Get a summary message for a reason code.
 * This is a fallback - UI should use i18n with summaryKey.
 */
function getReasonSummary(code: string, _data?: Record<string, unknown>): string {
  switch (code) {
    case 'MISSING_CAPABILITY':
      return `You don't have permission to perform this action.`;
    case 'NO_POLICY_MATCHED':
      return `This action is not allowed in the current context.`;
    case 'ADMIN_OVERRIDE':
      return `Allowed via admin override.`;
    case 'OWNER_EDIT_ALLOWED':
      return `You can edit your own content.`;
    case 'STEWARD_ACCESS':
      return `Allowed as the item's assigned owner.`;
    case 'ROLE_ALLOWED':
      return `Allowed based on your role.`;
    case 'NOT_OWNER':
      return `Only the owner can perform this action.`;
    case 'NOT_STEWARD':
      return `Only the item's assigned owner can perform this action.`;
    case 'RESOURCE_LOCKED':
      return `This item is locked and cannot be modified.`;
    case 'FIELD_LOCKED':
      return `This field is locked and cannot be modified.`;
    case 'ALREADY_PUBLISHED':
      return `This item has been published and cannot be modified.`;
    case 'SUBMISSION_WINDOW_CLOSED':
      return `The submission window is closed.`;
    case 'WRONG_PHASE':
      return `This action is not available in the current workflow context.`;
    case 'CONTRIBUTIONS_FROZEN':
      return `Contributions are currently frozen.`;
    case 'APPROVAL_REQUIRED':
      return `Approval is required before this action can proceed.`;
    case 'MISSING_PREREQUISITE':
      return `A required prerequisite is missing for this action.`;
    case 'ROLE_RESTRICTED':
      return `Your current role is not allowed to perform this action here.`;
    case 'DEADLINE_PASSED':
      return `The deadline for this action has passed.`;
    case 'NOT_YET_OPEN':
      return `This is not yet available.`;
    default:
      return `Action not allowed: ${code}`;
  }
}

/**
 * Get remediation hints based on denial reasons.
 */
function getRemediationHints(
  reasons: PolicyReason[]
): Array<{ key: string; message: string; data?: Record<string, unknown> }> {
  const hints: Array<{ key: string; message: string; data?: Record<string, unknown> }> = [];
  
  for (const reason of reasons) {
    switch (reason.code) {
      case 'NOT_OWNER':
        hints.push({
          key: 'contact-owner',
          message: 'Contact the owner to request changes.',
        });
        break;
      case 'NOT_STEWARD':
        hints.push({
          key: 'contact-steward',
          message: 'Contact the item\'s owner for assistance.',
        });
        break;
      case 'RESOURCE_LOCKED':
        hints.push({
          key: 'request-unlock',
          message: 'Request that the item be unlocked.',
        });
        break;
      case 'SUBMISSION_WINDOW_CLOSED':
        hints.push({
          key: 'wait-for-window',
          message: 'Wait for the next submission window to open.',
        });
        break;
      case 'WRONG_PHASE':
        hints.push({
          key: 'wait-for-phase',
          message: `This action will be available in a different workflow context.`,
          data: reason.data,
        });
        break;
      case 'MISSING_CAPABILITY':
        hints.push({
          key: 'request-permission',
          message: 'Contact an administrator to request this permission.',
        });
        break;
      case 'APPROVAL_REQUIRED':
        hints.push({
          key: 'request-approval',
          message: 'Ask an approver to review and approve this action.',
          data: reason.data,
        });
        break;
      case 'MISSING_PREREQUISITE':
        hints.push({
          key: 'satisfy-prerequisite',
          message: 'Complete the missing prerequisite, then try again.',
          data: reason.data,
        });
        break;
    }
  }
  
  return hints;
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Create a simple capability checker that always returns true.
 * Useful for testing policy rules in isolation.
 */
export function createPassthroughCapabilityChecker(): CapabilityChecker {
  return () => true;
}

/**
 * Create a capability checker from a set of allowed permissions.
 * Useful for testing.
 */
export function createCapabilityCheckerFromSet(
  allowedPermissions: Set<string>
): CapabilityChecker {
  return (action: string) => allowedPermissions.has(action);
}

/**
 * Evaluate and explain in one call.
 * Convenience function for UI components.
 */
export function evaluateAndExplain(
  actor: ActorContext,
  action: ActionContext,
  resource: ResourceContext,
  context: EvaluationContext,
  rules: PolicyRule[],
  capabilityChecker: CapabilityChecker
): { decision: PolicyDecision; explanation: ExplainResult } {
  const decision = evaluateAuthorization(
    actor,
    action,
    resource,
    context,
    rules,
    capabilityChecker
  );
  
  const explanation = explainDecision(decision);
  
  return { decision, explanation };
}
